import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const PROJECTS = [
  {
    id: 'aperture-slate',
    status: 'In Development',
    statusColor: 'text-amber-400 border-amber-400/30 bg-amber-400/10',
    category: 'Mobile Studio Build',
    title: 'Aperture Slate Mobile 6K Studio',
    subtitle: 'Blackmagic Films × Coleman 13B',
    description:
      'Converting a 2026 Coleman 13B into a fully off-grid, cinema-grade mobile interview studio. Powered by solar + lithium battery banks, equipped with a Blackmagic 6K rig, Nanoleaf smart lighting, and acoustic treatment — built to bring broadcast-quality production to wherever the talent is.',
    phases: ['Acquisition & Exterior', 'Power & Comm Command', 'Cinema Interior', 'Interview Series Launch'],
    currentPhase: 0,
    link: '/aperture-slate.html',
    linkLabel: 'View Sponsorship Deck →',
    external: true,
    image: '/images/mobile-slate.jpg',
    tags: ['Mobile Studio', '6K Cinema', 'Celebrity Interviews', 'Northeast Tour'],
  },
  {
    id: 'jersey-joint-420',
    status: 'Completed',
    statusColor: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10',
    category: 'Event Coverage',
    title: 'Jersey Joint Dispensary — 420 Fest',
    subtitle: 'April 19th, 2026',
    description:
      'Full event coverage of Jersey Joint Dispensary\'s 420 Fest — capturing the performances, crowd, and atmosphere of one of New Jersey\'s biggest cannabis culture celebrations.',
    phases: [],
    currentPhase: null,
    link: 'https://www.youtube.com/watch?v=unNDwilLvF4',
    linkLabel: 'Watch on YouTube →',
    external: true,
    tags: ['Jersey Joint', '420 Fest', 'Event Coverage', 'New Jersey'],
  },
  {
    id: 'gwar-secret-stash',
    status: 'Completed',
    statusColor: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10',
    category: 'On-Location Coverage',
    title: 'GWAR at The Secret Stash',
    subtitle: 'March 2026 — Red Bank, NJ',
    description:
      "On-location coverage of GWAR's appearance at Kevin Smith's Secret Stash comic book shop in Red Bank, NJ. Captured the full event experience — the band, the fans, and the chaos — in cinema quality.",
    phases: [],
    currentPhase: null,
    link: 'https://www.youtube.com/watch?v=63-anjYLujQ',
    linkLabel: 'Watch on YouTube →',
    external: true,
    tags: ['GWAR', 'Kevin Smith', 'The Secret Stash', 'On-Location', 'Event Coverage'],
  },
  {
    id: 'warped-tour',
    status: 'Completed',
    statusColor: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10',
    category: 'Concert Coverage',
    title: 'Warped Tour — Epitaph Records',
    subtitle: 'Concert Photography',
    description:
      'Official photography coverage of Warped Tour for Epitaph Records — capturing artists, crowds, and the energy of one of punk and alternative music\'s most iconic touring festivals.',
    phases: [],
    currentPhase: null,
    link: '/photography',
    linkLabel: 'View Gallery →',
    external: false,
    tags: ['Warped Tour', 'Epitaph Records', 'Concert Photography', 'Punk'],
  },
  {
    id: 'thrillrot',
    status: 'Completed',
    statusColor: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10',
    category: 'EPK',
    title: 'ThrillRot EPK',
    subtitle: 'Electronic Press Kit',
    description:
      'Full electronic press kit production for ThrillRot — including promo photography, event coverage, and a dedicated EPK page for press and booking inquiries.',
    phases: [],
    currentPhase: null,
    link: '/thrillrot',
    linkLabel: 'View EPK →',
    external: false,
    tags: ['Photography', 'EPK', 'Promo'],
  },
  {
    id: 'secret-stash',
    status: 'Completed',
    statusColor: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10',
    category: 'On-Location Shoot',
    title: "Kevin Smith's Secret Stash",
    subtitle: 'On-Location Coverage',
    description:
      "On-location production coverage at Kevin Smith's Secret Stash comic book shop in Red Bank, NJ — capturing the store, the culture, and behind-the-scenes moments with one of Hollywood's most iconic independent filmmakers.",
    phases: [],
    currentPhase: null,
    link: '/photography',
    linkLabel: 'View Gallery →',
    external: false,
    tags: ['Kevin Smith', 'On-Location', 'Photography', 'BTS'],
  },
  {
    id: 'wedding-content',
    status: 'Accepting Bookings',
    statusColor: 'text-red-400 border-red-400/30 bg-red-400/10',
    category: 'New Service',
    title: 'Wedding Content Creator',
    subtitle: 'Philadelphia & New Jersey',
    description:
      '4K cinematic wedding content for Instagram Reels and TikTok. A dedicated on-site creator captures the real, shareable moments of your day — delivered within 48 hours. Starter packages from $299.',
    phases: [],
    currentPhase: null,
    link: '/weddings',
    linkLabel: 'View Packages →',
    external: false,
    tags: ['Weddings', 'Reels', 'Philadelphia', 'New Jersey'],
  },
];

export default function Projects() {
  return (
    <>
    <Helmet>
      <title>Mobile Production Services | Blackmagic Films — NJ, PA & NYC</title>
      <meta name="description" content="Blackmagic Films offers full mobile production packages with 6K cinema cameras for on-location shoots across New Jersey, Philadelphia PA, and New York City. Artists, brands, and events." />
      <link rel="canonical" href="https://www.blackmagicfilms.com/mobile-production" />
      <meta property="og:title" content="Mobile Production Services | Blackmagic Films — NJ, PA & NYC" />
      <meta property="og:description" content="Full mobile 6K production packages for artists, brands and events across NJ, PA, and NYC." />
      <meta property="og:url" content="https://www.blackmagicfilms.com/mobile-production" />
      <meta property="og:image" content="https://www.blackmagicfilms.com/images/og-image.jpg" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.blackmagicfilms.com/" },
          { "@type": "ListItem", "position": 2, "name": "Projects", "item": "https://www.blackmagicfilms.com/mobile-production" }
        ]
      })}</script>
    </Helmet>
    <div className="min-h-screen bg-black text-white pt-16 sm:pt-20">

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
            Blackmagic Films
          </span>
          <h1 className="text-4xl sm:text-5xl font-black mt-3 mb-3">Projects</h1>
          <p className="text-neutral-400 max-w-xl">
            Current builds, active productions, and completed work — what we're making and where we're going.
          </p>
        </motion.div>
      </div>

      {/* Projects list */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-24 space-y-6">
        {PROJECTS.map((project, i) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="bg-neutral-950 rounded-2xl ring-1 ring-white/10 p-6 sm:p-8 hover:ring-white/20 transition-all duration-300"
          >
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${project.statusColor}`}>
                  {project.status}
                </span>
                <span className="text-xs text-white/30 uppercase tracking-widest">{project.category}</span>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-black mb-1">{project.title}</h2>
            <p className="text-amber-400 text-sm font-medium mb-4">{project.subtitle}</p>
            <p className="text-neutral-400 leading-relaxed mb-5 max-w-3xl">{project.description}</p>

            {/* Studio render image */}
            {project.image && (
              <div className="mb-6 rounded-xl overflow-hidden ring-1 ring-white/10">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>
            )}

            {/* Phase tracker */}
            {project.phases.length > 0 && (
              <div className="mb-6">
                <p className="text-xs text-white/30 uppercase tracking-widest mb-3">Build Phases</p>
                <div className="flex flex-wrap gap-2">
                  {project.phases.map((phase, idx) => (
                    <div
                      key={phase}
                      className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border ${
                        idx === project.currentPhase
                          ? 'bg-amber-400/10 border-amber-400/30 text-amber-400'
                          : idx < project.currentPhase
                          ? 'bg-emerald-400/10 border-emerald-400/20 text-emerald-400/70'
                          : 'bg-white/5 border-white/10 text-white/30'
                      }`}
                    >
                      <span>{idx < project.currentPhase ? '✓' : idx === project.currentPhase ? '●' : '○'}</span>
                      Phase {idx + 1}: {phase}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {project.tags.map(tag => (
                <span key={tag} className="text-xs bg-white/5 border border-white/10 px-3 py-1 rounded-full text-white/40">
                  {tag}
                </span>
              ))}
            </div>

            {/* CTA */}
            {project.external ? (
              <a
                href={project.link}
                className="inline-flex items-center gap-2 text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors"
              >
                {project.linkLabel}
              </a>
            ) : (
              <Link
                to={project.link}
                className="inline-flex items-center gap-2 text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors"
              >
                {project.linkLabel}
              </Link>
            )}
          </motion.div>
        ))}
      </div>
    </div>
    </>
  );
}
